import React from 'react';
import { EarIcon, FlagIcon, ShuffleIcon, ChevronRightIcon } from 'lucide-react';
import { Section, Kicker } from './Section';
import { FlowchartCard } from './FlowchartCard';
import { CtaButton } from './CtaButton';
import { TrustRow } from './TrustRow';
const steps = [{
  icon: EarIcon,
  title: 'Screen',
  body: "You don't run an intake. In the eight seconds after a student volunteers something, you ask the one question only they can answer."
}, {
  icon: FlagIcon,
  title: 'Flag',
  body: 'Check the refer-out signals, at two levels: stop now, or talk after class. Nothing to diagnose, only a line to notice.'
}, {
  icon: ShuffleIcon,
  title: 'Adapt',
  body: "Open the posture family today's plan already uses, take the variation, and say the line — in your style, mid-class."
}];
const outputs = [{
  color: '#2D6A4F',
  label: 'Green',
  text: 'Offer the variation and teach today'
}, {
  color: '#B5830A',
  label: 'Amber',
  text: 'Conservative options now, talk after class'
}, {
  color: '#9B2226',
  label: 'Red',
  text: 'Stop now — refer out, in the words that keep the student'
}];
export function Mechanism() {
  return <Section background="white" labelledBy="mechanism-heading" data-id="element-185" data-mp="fmss6rzqr6u0">
      <div className="text-center" data-id="element-186" data-mp="fkzlxdt1iomj">
        <Kicker data-id="element-187" data-mp="bl7cyzzqiggn">The mechanism</Kicker>
        <h2 id="mechanism-heading" className="mt-5 font-serif text-[28px] font-semibold leading-[1.15] text-ink sm:text-3xl lg:text-[44px]" data-id="element-188" data-mp="bk5uiage2row">
          Screen → Flag → Adapt
        </h2>
        <p className="mt-4 font-sans text-[17px] text-ink-soft sm:text-lg" data-id="element-189" data-mp="1lgfcpfvnlcc">
          Three moves. Under ninety seconds. Thirty conditions, nine populations, any style.
        </p>
      </div>

      <ol className="mt-14 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[1fr_auto_1fr_auto_1fr]" data-id="element-190" data-mp="3ot27p6asnsqd">
        {steps.map(({
        icon: Icon,
        title,
        body
      }, index) => <React.Fragment key={title} data-id="element-191" data-mp="1mzunbigbqkxm" data-mpi={index}>
            {index > 0 && <ChevronRightIcon className="mx-auto hidden h-6 w-6 self-center text-sage/45 lg:block" strokeWidth={1.5} aria-hidden="true" data-id="element-192" data-mp="1myjjjimvpd6i" />}
            <li className="flex h-full flex-col rounded-2xl border border-sage/15 bg-sage-soft p-6" data-id="element-193" data-mp="37iolaiw6aj94">
              <div className="flex items-center gap-3" data-id="element-194" data-mp="3bjqlp31tt1co">
                <Icon className="h-6 w-6 text-sage" strokeWidth={1.5} aria-hidden="true" data-id="element-195" data-mp="3bk9t3ptuo7cg" />
                <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-brass" data-id="element-196" data-mp="34q4j444jd3q">
                  Step {index + 1}
                </span>
              </div>
              <h3 className="mt-4 font-serif text-2xl font-semibold uppercase tracking-tight text-ink" data-id="element-197" data-mp="3q84pphazavut">
                {title}
              </h3>
              <p className="mt-3 font-sans text-[15px] leading-[1.6] text-ink-soft" data-id="element-198" data-mp="2etr3egvviogw">{body}</p>
            </li>
          </React.Fragment>)}
      </ol>

      <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-199" data-mp="2juf5pypjyrqf">
        {outputs.map(output => <li key={output.label} className="flex items-start gap-3 rounded-xl border border-sage/15 bg-white px-4 py-3.5" data-id="element-200" data-mp="2ba8trguvgg8i">
            <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{
          backgroundColor: output.color
        }} aria-hidden="true" data-id="element-201" data-mp="2laj5ex19dflk" />
            <p className="font-sans text-[14px] leading-snug text-ink-soft" data-id="element-202" data-mp="1sl2gwagreium">
              <span className="font-semibold uppercase tracking-[0.08em]" style={{
            color: output.color
          }} data-id="element-203" data-mp="1sizhx0xmpfdr">
                {output.label}
              </span>{' '}
              — {output.text}
            </p>
          </li>)}
      </ul>

      <h3 className="mt-20 text-center font-serif text-2xl font-semibold leading-snug text-ink sm:text-3xl" data-id="element-204" data-mp="1xic5xp08kshx">
        This is the actual decision flow from Volume 1.
      </h3>
      <FlowchartCard data-id="element-205" data-mp="14v85zyg034d0" />

      <div className="mt-14 flex flex-col items-center" data-id="element-206" data-mp="14tnbg8torlf5">
        <CtaButton href="#pricing" data-id="element-207" data-mp="85fh5hs21kae">Show me the system</CtaButton>
        <TrustRow className="mt-4 justify-center" data-id="element-208" data-mp="d37kd7y5gstw" />
      </div>
    </Section>;
}